import clsx from 'clsx';
import React, {ReactElement, useContext, useState} from 'react';
import {ReactComponent as PrintIcon} from '../../../../../../../assets/images/icon-print.svg';
import {PrintType} from '../../../../../../../shared/model/print.model';
import IconButtonSwitch from '../../../../../../shared/icon-button-switches/IconButtonSwitch';
import {SystemContext} from '../../../../../../shared/system-context-provider/SystemContextProvider';
import styles from './PrintButton.module.scss';
import usePrint from './usePrint';
import {NxButton, NxButtonVariant} from '@nextbank/ui-components';
import NxPopup from '../../../../../../shared/nx-popup/NxPopup';
import {Trans} from 'react-i18next';

interface Props {
  applicationId: number;
  phaseId: number;
  disabled: boolean;
  printType: PrintType[];
}

export const PrintButton = ({applicationId, phaseId, disabled, printType}: Props): ReactElement => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedPrintType, setSelectedPrintType] = useState<PrintType | null>(null);
  const {printsEnabled} = useContext(SystemContext);

  const {onPrint: doPrint, isGenerating} = usePrint(applicationId, phaseId, selectedPrintType as PrintType);

  const onPrint = async (type?: PrintType) => {
    const printTypeToUse = type || selectedPrintType;
    if (printTypeToUse) {
      await doPrint(printTypeToUse);
    }
  };

  const className = clsx(
    styles.printButton,
    {
      [styles.buttonDisabled]: disabled
    }
  );

  const handlePrintClick = async () => {
    if (printType.length > 1) {
      setIsPopupOpen(true);
    } else if (printType.length === 1) {
      setSelectedPrintType(printType[0]);
      await onPrint(printType[0]);
    }
  };

  const handlePrintTypeSelect = (type: PrintType) => {
    setSelectedPrintType(type);
    onPrint(type);
    setIsPopupOpen(false);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  };

  return (
    <>
      {printsEnabled && (
        <>
          <IconButtonSwitch
            icon={<PrintIcon />}
            onClick={handlePrintClick}
            className={className}
            disabled={isGenerating || disabled || printType.length === 0}
            loaded={!isGenerating}
          />

          {isPopupOpen && (
            <NxPopup
              open={isPopupOpen}
              header="Print Options"
              footerClassName={styles.popupFooter}
            >
              <div className={styles.printTypeButtons}>
                {printType.map((type) => (
                  <NxButton
                    key={type}
                    onClick={() => handlePrintTypeSelect(type)}
                    variant={NxButtonVariant.CONTAINED}
                  >
                    <Trans i18nKey={`LOAN_STEP_PRINT_LABELS.${type}`} />
                  </NxButton>
                ))}
                <NxButton
                  onClick={handleClosePopup}
                  variant={NxButtonVariant.TEXT}
                >
                  <Trans>COMMON.CLOSE</Trans>
                </NxButton>
              </div>
            </NxPopup>
          )}
        </>
      )}
    </>
  );
};
